import type { ExitChecklistTaskDto } from "./types";

/** Checklist / interview edits allowed only in these exit statuses (API returns 400 otherwise). */
export const EXIT_CHECKLIST_EDIT_STATUSES = new Set([
  "MANAGER_APPROVED",
  "HR_REVIEW",
  "NOTICE_PERIOD",
  "EXIT_IN_PROGRESS",
]);

export function exitChecklistEditingAllowed(exitStatus: string | undefined): boolean {
  if (!exitStatus) return false;
  return EXIT_CHECKLIST_EDIT_STATUSES.has(exitStatus);
}

/** POST apply-question-set only in these phases (API returns 400 otherwise). */
const EXIT_INTERVIEW_APPLY_STATUSES = new Set([
  "MANAGER_APPROVED",
  "HR_REVIEW",
  "NOTICE_PERIOD",
  "EXIT_IN_PROGRESS",
]);

export function exitInterviewQuestionSetApplyAllowed(
  exitStatus: string | undefined,
): boolean {
  if (!exitStatus) return false;
  return EXIT_INTERVIEW_APPLY_STATUSES.has(exitStatus);
}

/** PATCH responses / POST submit until locked, in these phases. */
const EXIT_INTERVIEW_RESPONSE_EDIT_STATUSES = new Set([
  "MANAGER_APPROVED",
  "HR_REVIEW",
  "NOTICE_PERIOD",
  "EXIT_IN_PROGRESS",
  "FINAL_SETTLEMENT",
]);

export function exitInterviewResponsesEditingAllowed(
  exitStatus: string | undefined,
): boolean {
  if (!exitStatus) return false;
  return EXIT_INTERVIEW_RESPONSE_EDIT_STATUSES.has(exitStatus);
}

function isTaskDoneOrWaived(t: ExitChecklistTaskDto): boolean {
  return t.status === "COMPLETED" || t.status === "WAIVED";
}

/**
 * Tasks with numeric sequence block completing task S until all tasks with sequence < S
 * are Completed or Waived. Tasks without sequence do not participate in ordering.
 */
export function exitChecklistSequenceBlocksTask(
  tasks: ExitChecklistTaskDto[] | undefined,
  task: ExitChecklistTaskDto,
): { blocked: boolean; blockingTaskNames: string[] } {
  if (!tasks?.length) return { blocked: false, blockingTaskNames: [] };
  const seq = task.sequence;
  if (seq == null || Number.isNaN(Number(seq))) {
    return { blocked: false, blockingTaskNames: [] };
  }
  const n = Number(seq);
  const blocking: string[] = [];
  for (const t of tasks) {
    if (t.id === task.id) continue;
    const ts = t.sequence;
    if (ts == null || Number.isNaN(Number(ts))) continue;
    if (Number(ts) < n && !isTaskDoneOrWaived(t)) {
      blocking.push(t.taskName || t.id);
    }
  }
  return { blocked: blocking.length > 0, blockingTaskNames: blocking };
}

export function exitInterviewRequired(
  requirements: { requireInterviewSubmitted?: boolean } | null | undefined,
): boolean {
  return requirements?.requireInterviewSubmitted !== false;
}

export function exitSettlementRequired(
  requirements: { requireSettlementCompleted?: boolean } | null | undefined,
): boolean {
  return requirements?.requireSettlementCompleted !== false;
}
export function exitSettlementEditingAllowed(
  exitStatus: string | undefined,
): boolean {
  if (!exitStatus) return false;
  return ["EXIT_IN_PROGRESS", "FINAL_SETTLEMENT"].includes(exitStatus);
}
